import type {ValueOrderType} from "@reva/shared/value-order";

import {CommissionValueOrderApiInterface} from "../api/interface/commission-value-order";
import {
    AttachValueOrderApiInterface,
    DetachValueOrderApiInterface,
    type OrderTypeAttachable,
} from "../api/interface/value-order-attachment";
import type {CommissionValueOrder} from "../api/payload-types/commission-value-order";
import {ErrorI18n} from "../i18n";
import {selectCredentials} from "./authSlice";
import {selectLoadedSnapshotNumber, selectRevaId} from "./persistenceSlice";
import {handleValidPersistenceResponseReceived} from "./persistenceThunk";
import type {RevaThunkAction} from "./revaHooks";

/**
 * Get OrderType URL Parameter for attach/detach value-order.
 */
function _resolveOrderTypeForUrl(orderType: ValueOrderType): OrderTypeAttachable | null {
    switch (orderType) {
        case "Inspection":
            return "inspection";
        default:
            return null;
    }
}

/**
 * Async thunk to attach a value order to the current valuation.
 * @param externalOrderNumber external order number of the value order to attach
 * @param orderType order type to attach
 */
export const attachValueOrder =
    (externalOrderNumber: string, orderType: ValueOrderType): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());
        if (!revaId || !externalOrderNumber || !orderType) {
            console.log("Thunk attachValueOrder called with incomplete arguments: ", {
                revaId,
                externalOrderNumber,
                orderType,
            });
            throw new ErrorI18n("general.error.unspecific");
        }

        const urlOrderType = _resolveOrderTypeForUrl(orderType);
        if (!urlOrderType) {
            console.log(`Thunk attachValueOrder called with invalid order type: ${orderType}`);
            throw new ErrorI18n("general.error.unspecific");
        }

        // If a snapshot is loaded, tell the backend so it frames the response as that snapshot instead
        // of switching the client to the live state (null when live → query param is omitted).
        const snapshotNumber = selectLoadedSnapshotNumber(getState());
        const response = await new AttachValueOrderApiInterface(
            revaId,
            externalOrderNumber,
            urlOrderType,
            snapshotNumber,
        ).execute(serviceApi, selectCredentials(getState())!);
        dispatch(handleValidPersistenceResponseReceived(response));
    };

/**
 * Async thunk to detach a value order from the current valuation.
 * @param valueOrderGuid orderGuid of the value order to detach
 * @param orderType order type to detach
 */
export const detachValueOrder =
    (valueOrderGuid: string, orderType: ValueOrderType): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());
        if (!revaId || !valueOrderGuid || !orderType) {
            console.log("Thunk detachValueOrder called with incomplete arguments: ", {
                revaId,
                valueOrderGuid,
                orderType,
            });
            throw new ErrorI18n("general.error.unspecific");
        }

        const urlOrderType = _resolveOrderTypeForUrl(orderType);
        if (!urlOrderType) {
            console.log(`Thunk detachValueOrder called with invalid order type: ${orderType}`);
            throw new ErrorI18n("general.error.unspecific");
        }

        // If a snapshot is loaded, tell the backend so it frames the response as that snapshot instead
        // of switching the client to the live state (null when live → query param is omitted).
        const snapshotNumber = selectLoadedSnapshotNumber(getState());
        const response = await new DetachValueOrderApiInterface(
            revaId,
            valueOrderGuid,
            urlOrderType,
            snapshotNumber,
        ).execute(serviceApi, selectCredentials(getState())!);
        dispatch(handleValidPersistenceResponseReceived(response));
    };

/**
 * Async thunk to create a value order for the current valuation.
 * @param order the value order to commission
 */
export const commissionValueOrder =
    (order: CommissionValueOrder): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());
        if (!revaId || !order.orderType) {
            throw new ErrorI18n("general.error.unspecific");
        }

        // If a snapshot is loaded, tell the backend so it frames the response as that snapshot instead
        // of switching the client to the live state (null when live → query param is omitted).
        const snapshotNumber = selectLoadedSnapshotNumber(getState());
        const response = await new CommissionValueOrderApiInterface(revaId, order, snapshotNumber).execute(
            serviceApi,
            selectCredentials(getState())!,
        );
        dispatch(handleValidPersistenceResponseReceived(response));
    };
